/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useAuth0 } from "~/components/Auth/auth";
import { useShowError } from "~/components/Error/useShowError";
import { UserRole } from "~/models";

type UserRoleCtx = {
  role: UserRole;
};

type UserProviderProps = {
  children: React.ReactNode;
};
const parseJwt = (token: string) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};
export function createCtx<ContextType>() {
  const ctx = React.createContext<ContextType | undefined>(undefined);

  function useCtx() {
    const c = React.useContext(ctx);
    if (!c) throw new Error("useCtx must be inside a Provider with a value");

    return c;
  }

  return [useCtx, ctx.Provider] as const;
}

export const [useUserConsumer, CtxProvider] = createCtx<UserRoleCtx>();

export function UserProvider({ children }: UserProviderProps) {
  const { isLoading, getIdTokenClaims } = useAuth0();
  const { showError } = useShowError();

  const [userRole, setUserRole] = React.useState(UserRole.BASIC_USER);

  const getTokenFromAuth = React.useCallback(async () => {
    try {
      const getToken = await getIdTokenClaims();
      const token = getToken?.__raw;
      const parse = parseJwt(token);
      const role = parse["https://cmdb/claims/permission"];

      switch (role) {
        case UserRole.SUPER_ADMIN:
          setUserRole(UserRole.SUPER_ADMIN);
          break;
        case UserRole.PRIVILEGED_USER:
          setUserRole(UserRole.PRIVILEGED_USER);
          break;
        case UserRole.STANDARD_USER:
          setUserRole(UserRole.STANDARD_USER);
          break;
        default:
          setUserRole(UserRole.BASIC_USER);
      }
    } catch (e) {
      showError(e.response);
    }
  }, [getIdTokenClaims, showError]);

  React.useEffect(() => {
    !isLoading && getTokenFromAuth();
  }, [getTokenFromAuth, isLoading]);

  return <CtxProvider value={{ role: userRole }}>{children}</CtxProvider>;
}
